import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

// Groups used to each get their own hue purely as a category identifier —
// five independent, parallel groups, no urgency difference between them — so
// there's no per-group `color` field anymore; the render below uses one
// consistent var(--accent) "enabled" treatment for all of them (see
// CAERA_DASHBOARD_REDESIGN_SPEC.md §9).
const NOTIFICATION_TYPES = [
  {
    group: 'Tasks',
    items: [
      { type: 'task_assigned',  label: 'Task assigned to me',        desc: 'Email when a task is assigned to you' },
      { type: 'task_completed', label: 'Task completed',              desc: 'Email when your assigned task is marked done' },
      { type: 'task_reminder',  label: 'Overdue task reminders',      desc: 'Reminder every 3 days for overdue tasks' },
    ],
  },
  {
    group: 'Patients & Clinical',
    items: [
      { type: 'stage_change',      label: 'Patient stage changes',    desc: 'Email when a patient you manage moves to a new stage' },
      { type: 'new_intake',        label: 'New intake submissions',   desc: 'Email when a new patient intake form is submitted' },
      { type: 'nurse_assessment',  label: 'Assessment overdue alerts',desc: 'Alert when a nurse assessment is overdue by 7+ days' },
    ],
  },
  {
    group: 'Authorisations',
    items: [
      { type: 'auth_expiry', label: 'PA/DMA expiry alerts', desc: 'Alerts at 60, 30, and 7 days before expiry' },
    ],
  },
  {
    group: 'Visits',
    items: [
      { type: 'missed_visit', label: 'Missed visit alerts', desc: 'Email when a visit is marked as missed' },
    ],
  },
  {
    group: 'Digest',
    items: [
      { type: 'daily_digest', label: 'Daily morning digest', desc: 'Summary email at 7am with overdue items, expiring auths, and pending assessments' },
    ],
  },
];

export default function NotificationPreferences() {
  const { profile } = useAuth();
  const [prefs,   setPrefs]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => { if (profile?.id) loadPrefs(); }, [profile]);

  const loadPrefs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('notification_preferences')
      .select('notification_type, enabled')
      .eq('profile_id', profile.id);

    const map = {};
    // Default all to true if no preference exists
    NOTIFICATION_TYPES.flatMap(g => g.items).forEach(({ type }) => { map[type] = true; });
    (data || []).forEach(({ notification_type, enabled }) => { map[notification_type] = enabled; });
    setPrefs(map);
    setLoading(false);
  };

  const toggle = async (type) => {
    const newVal = !prefs[type];
    setSaving(type);
    setPrefs(p => ({ ...p, [type]: newVal }));

    const { error } = await supabase
      .from('notification_preferences')
      .upsert({ profile_id: profile.id, notification_type: type, enabled: newVal }, { onConflict: 'profile_id,notification_type' });

    if (error) {
      setPrefs(p => ({ ...p, [type]: !newVal })); // revert
    } else {
      setSuccess(`${newVal ? 'Enabled' : 'Disabled'} — saved`);
      setTimeout(() => setSuccess(''), 2000);
    }
    setSaving(null);
  };

  const toggleAll = async (enabled) => {
    const allTypes = NOTIFICATION_TYPES.flatMap(g => g.items.map(i => i.type));
    setPrefs(Object.fromEntries(allTypes.map(t => [t, enabled])));
    await supabase.from('notification_preferences').upsert(
      allTypes.map(type => ({ profile_id: profile.id, notification_type: type, enabled })),
      { onConflict: 'profile_id,notification_type' }
    );
    setSuccess(enabled ? 'All notifications enabled' : 'All notifications disabled');
    setTimeout(() => setSuccess(''), 2500);
  };

  const enabledCount = Object.values(prefs).filter(Boolean).length;
  const totalCount   = NOTIFICATION_TYPES.flatMap(g => g.items).length;

  return (
    <div>
      {success && (
        <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '9px 14px', marginBottom: 12, fontSize: 12, color: 'var(--success)' }}>
          ✓ {success}
        </div>
      )}

      {/* Header card */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Email Notification Preferences</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              Emails sent to <strong style={{ color: 'var(--text2)' }}>{profile?.email || '—'}</strong>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{enabledCount}/{totalCount} enabled</span>
            <button className="btn btn-outline btn-sm" onClick={() => toggleAll(false)}>Disable all</button>
            <button className="btn btn-primary btn-sm" onClick={() => toggleAll(true)}>Enable all</button>
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <div style={{ padding: '4px 0' }}>
            {NOTIFICATION_TYPES.map(group => (
              <div key={group.group}>
                {/* Group header */}
                <div style={{ padding: '12px 16px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 3, height: 14, background: 'var(--accent)', borderRadius: 2 }} />
                  <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)' }}>{group.group}</span>
                </div>

                {group.items.map(({ type, label, desc }) => {
                  const enabled  = prefs[type] ?? true;
                  const isSaving = saving === type;
                  return (
                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderBottom: '0.5px solid var(--border)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                      {/* Toggle */}
                      <button
                        onClick={() => !isSaving && toggle(type)}
                        style={{
                          width: 42, height: 24, borderRadius: 12, border: 'none', cursor: isSaving ? 'wait' : 'pointer',
                          background: enabled ? 'var(--accent)' : 'var(--border2)',
                          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: 3, left: enabled ? 21 : 3,
                          width: 18, height: 18, borderRadius: '50%', background: '#fff',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </button>

                      {/* Label */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: enabled ? 'var(--text)' : 'var(--text3)' }}>{label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{desc}</div>
                      </div>

                      {/* Status */}
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500, background: enabled ? 'var(--accent-bg)' : 'var(--bg2)', color: enabled ? 'var(--accent)' : 'var(--text3)', flexShrink: 0 }}>
                        {isSaving ? '...' : enabled ? 'On' : 'Off'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--text2)' }}>When are emails sent?</strong><br />
        Immediate notifications (task assigned, stage change, new intake) are sent in real time.<br />
        Daily digest arrives at <strong style={{ color: 'var(--text2)' }}>7:00 AM</strong> each morning.<br />
        Scheduled checks (task reminders, auth expiry, assessment alerts) run daily at <strong style={{ color: 'var(--text2)' }}>8:00 AM</strong>.
      </div>
    </div>
  );
}
